import { WorkflowStepErrorResponse } from '@shared/models';

/**
 * KOR's codes for a financial account whose currency the debit authorization cannot use.
 *
 * The select and link steps raise these from their own guards; the debit authorization step raises a
 * field error on `currency` instead, so both shapes are recognised below. Before KOR-14968 none of
 * this existed - financial-account-flow answered 400 and the customer saw "something went wrong".
 */
export const CURRENCY_ERROR_CODES: ReadonlySet<string> = new Set([
  'selected-financial-account-currency-invalid',
  'linked-financial-account-currency-invalid',
]);

/** Where the debit authorization step's field error puts the currency: `<stepName>/currency`. */
const CURRENCY_FIELD_PATH = /(?:^|\/)currency$/u;

const bodyOf = (errorRes: unknown): WorkflowStepErrorResponse =>
  ((errorRes as { error?: WorkflowStepErrorResponse } | undefined)?.error ?? {}) as WorkflowStepErrorResponse;

const currencyFieldError = (body: WorkflowStepErrorResponse) =>
  (body.errors ?? []).find((fieldError) => CURRENCY_FIELD_PATH.test(fieldError.path ?? ''));

/**
 * The customer-facing message for a currency rejection, or `undefined` when this is some other
 * failure.
 *
 * Deliberately narrow: only these two shapes are unwrapped, so an unrecognised server error still
 * falls back to the caller's own wording rather than showing the customer whatever KOR happened to
 * put in `message`.
 */
export const debitAuthorizationCurrencyError = (errorRes: unknown): string | undefined => {
  const body = bodyOf(errorRes);

  if (CURRENCY_ERROR_CODES.has(body.errorCode ?? '')) {
    return body.message ?? DEFAULT_CURRENCY_MESSAGE;
  }

  const fieldError = currencyFieldError(body);

  return fieldError ? (fieldError.message ?? DEFAULT_CURRENCY_MESSAGE) : undefined;
};

/** Used when KOR names the currency as the problem but sends no message of its own. */
export const DEFAULT_CURRENCY_MESSAGE =
  'This account is in a different currency from the authorization. Please choose an account in the authorization currency.';
